// src/pages/KarigarManagement/Job/components/ProductImageCard.jsx

import React from "react";
import { Button } from "@/components/ui-v2/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui-v2/tooltip";
import { Eye, Trash2, RotateCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Individual product image card with remove/restore functionality
 */
const ProductImageCard = ({
  url,
  index,
  onRemove,
  onPreview,
  isRemoved = false,
  label = "Product",
  removedLabel = "Removed",
}) => {
  return (
    <div
      className={cn(
        "relative group aspect-square rounded-lg overflow-hidden border-2 transition-all",
        isRemoved
          ? "border-red-300 opacity-50"
          : "border-purple-200 hover:border-purple-400"
      )}
    >
      <img
        src={url}
        alt={`${label} ${index + 1}`}
        className="w-full h-full object-cover"
      />

      {/* Overlay with actions */}
      <div
        className={cn(
          "absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2",
          isRemoved && "bg-red-500/20"
        )}
      >
        {!isRemoved ? (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview?.(url);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Preview</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.(index);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.(index);
                    }}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Restore</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {/* Label badge */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 text-white text-xs py-0.5 text-center",
          isRemoved ? "bg-red-600/80" : "bg-purple-600/80"
        )}
      >
        {isRemoved ? removedLabel : label}
      </div>

      {/* Removed X indicator */}
      {isRemoved && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <X className="h-12 w-12 text-red-500/50" />
        </div>
      )}
    </div>
  );
};

export default ProductImageCard;
